import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerLogicalOperator,
    ComposerPricingNode,
    getComposerLinkOptionIDs
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

// I PREZZI CONDIVISI DELLA SELEZIONE MULTIPLA (utente, 2026-10-21), come le azioni condivise: un prezzo condiviso non è
// un oggetto a sé, sono le righe di prezzo delle opzioni scelte che hanno le stesse condizioni. Il motore valuta ogni
// opzione per conto suo, e l'API salva una riga per opzione: è lo stesso dato che si ottiene mettendo il prezzo a mano.

export type ComposerSharedPriceEntry = { attributeID: string; index: number };

export type ComposerSharedOptionPrice = {
    key: string;
    representative: ComposerPricingNode;
    entries: ComposerSharedPriceEntry[];
    hasMixedValues: boolean;
};

/** La chiave di un prezzo sono le sue condizioni, senza l'importo: è la parte che si vuole cambiare insieme. */
export const getComposerPriceConditionKey = (pricing: ComposerPricingNode): string => {
    const links = (pricing.links ?? []).map((link) => [link.attributeID, link.operation, getComposerLinkOptionIDs(link).sort().join(';')].join('|')).sort();
    return JSON.stringify([links.length > 1 ? (pricing.linkOperation ?? ComposerLogicalOperator.AND) : '', links]);
};

const pricingsOf = (option: ComposerAttributeOptionNode, attributes: ComposerAttributeNode[]) => {
    const attribute = attributes.find((candidate) => candidate.id === option.attributeID);
    return (attribute?.pricings ?? [])
        .map((pricing, index) => ({ pricing, entry: { attributeID: option.attributeID, index } }))
        .filter(({ pricing }) => pricing.selectedOptionGuid === option.id);
};

/** Solo i prezzi che si abbinano senza indovinare: ogni opzione ne ha esattamente uno con quelle condizioni. */
export const getComposerSharedOptionPrices = (options: ComposerAttributeOptionNode[], attributes: ComposerAttributeNode[]): ComposerSharedOptionPrice[] => {
    if (options.length < 2) return [];
    const byOption = options.map((option) => {
        const byKey = new Map<string, ReturnType<typeof pricingsOf>>();
        pricingsOf(option, attributes).forEach((item) => {
            const key = getComposerPriceConditionKey(item.pricing);
            byKey.set(key, [...(byKey.get(key) ?? []), item]);
        });
        return byKey;
    });
    return Array.from(byOption[0].keys()).flatMap((key) => {
        const matches = byOption.map((byKey) => byKey.get(key) ?? []);
        if (matches.some((items) => items.length !== 1)) return [];
        const items = matches.map(([item]) => item);
        return [
            {
                key,
                representative: items[0].pricing,
                entries: items.map((item) => item.entry),
                hasMixedValues: new Set(items.map((item) => Number(item.pricing.price) || 0)).size > 1
            }
        ];
    });
};

/** Gli attributi toccati, ciascuno con le sue righe di prezzo riscritte. */
const rewrite = (
    attributes: ComposerAttributeNode[],
    entries: ComposerSharedPriceEntry[],
    change: (pricing: ComposerPricingNode) => ComposerPricingNode | null
): ComposerAttributeNode[] =>
    Array.from(new Set(entries.map((entry) => entry.attributeID))).flatMap((attributeID) => {
        const attribute = attributes.find((candidate) => candidate.id === attributeID);
        if (!attribute) return [];
        const touched = new Set(entries.filter((entry) => entry.attributeID === attributeID).map((entry) => entry.index));
        const pricings = attribute.pricings.flatMap((pricing, index) => {
            if (!touched.has(index)) return [pricing];
            const next = change(pricing);
            return next ? [next] : [];
        });
        return [{ ...attribute, pricings }];
    });

/** Importo e condizioni scritti in tutte le opzioni; identità e dati di salvataggio di ogni riga restano suoi. */
export const applyComposerSharedPriceEdit = (
    attributes: ComposerAttributeNode[],
    entries: ComposerSharedPriceEntry[],
    patch: Pick<Partial<ComposerPricingNode>, 'price' | 'links' | 'linkOperation'>
) => rewrite(attributes, entries, (pricing) => ({ ...pricing, ...patch }));

export const removeComposerSharedPrice = (attributes: ComposerAttributeNode[], entries: ComposerSharedPriceEntry[]) => rewrite(attributes, entries, () => null);

/**
 * UN PREZZO «ALWAYS» IN TUTTE LE OPZIONI SCELTE. Un'opzione ne ha al più uno senza condizioni: dove c'è già si tiene quello
 * (la card condivisa lo mostra, con «Mixed values» finché gli importi sono diversi); dove manca, nasce a zero.
 */
export const addComposerSharedPrice = (options: ComposerAttributeOptionNode[], attributes: ComposerAttributeNode[]): ComposerAttributeNode[] => {
    const always = getComposerPriceConditionKey({ links: [] } as unknown as ComposerPricingNode);
    const missing = options.filter((option) => !pricingsOf(option, attributes).some(({ pricing }) => getComposerPriceConditionKey(pricing) === always));
    return Array.from(new Set(missing.map((option) => option.attributeID))).flatMap((attributeID) => {
        const attribute = attributes.find((candidate) => candidate.id === attributeID);
        if (!attribute) return [];
        const added: ComposerPricingNode[] = missing
            .filter((option) => option.attributeID === attributeID)
            .map((option) => ({
                attributePricingID: 0,
                selectedOptionID: option.idNumeric || 0,
                selectedOptionGuid: option.id,
                selectedValue: '',
                price: 0,
                pricingID: 0,
                name: option.name,
                links: [],
                linkOperation: ComposerLogicalOperator.AND
            }));
        return [{ ...attribute, pricings: [...attribute.pricings, ...added] }];
    });
};
